// Where the call panel sits, as pure arithmetic.
//
// Separated from the hook because this is the part that is easy to get subtly wrong — a panel that
// can be dragged half off the screen, or that reappears off-screen on a smaller window — and it is
// the part a test can actually pin. No DOM, no React.

/** Gap kept between the panel and the edge it snaps to. Matches the old `bottom-4 right-4`. */
export const PANEL_MARGIN = 16

/** How close to the top or bottom counts as "put it in the corner". */
export const SNAP_PX = 48

export interface Point {
  left: number
  top: number
}

export interface Size {
  width: number
  height: number
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max))
}

/**
 * Keep the panel fully on screen.
 *
 * Used while dragging AND on every resize: a window that shrinks below the panel's last position
 * would otherwise strand it outside the viewport, where an operator can neither see the wearer nor
 * reach Hang up.
 */
export function clampToViewport(pos: Point, panel: Size, viewport: Size, margin = PANEL_MARGIN): Point {
  return {
    left: clamp(pos.left, margin, viewport.width - panel.width - margin),
    top: clamp(pos.top, margin, viewport.height - panel.height - margin),
  }
}

/**
 * Where the panel lands when the operator lets go.
 *
 * It snaps to the nearer SIDE, always — that is what makes it feel like a video-call window rather
 * than a floating box, and it guarantees the panel never ends up straddling the middle of the map
 * the operator is trying to read. Vertically it stays where it was put, except within `SNAP_PX` of
 * the top or bottom, where it goes flush so the corners are easy to hit.
 */
export function snapToEdge(pos: Point, panel: Size, viewport: Size, margin = PANEL_MARGIN, snap = SNAP_PX): Point {
  const { left, top } = clampToViewport(pos, panel, viewport, margin)
  const maxLeft = Math.max(margin, viewport.width - panel.width - margin)
  const maxTop = Math.max(margin, viewport.height - panel.height - margin)
  const centre = left + panel.width / 2

  return {
    left: centre < viewport.width / 2 ? margin : maxLeft,
    top: top <= margin + snap ? margin : top >= maxTop - snap ? maxTop : top,
  }
}

/** The corner it starts in, matching where the panel has always appeared. */
export function defaultPosition(panel: Size, viewport: Size, margin = PANEL_MARGIN): Point {
  return {
    left: Math.max(margin, viewport.width - panel.width - margin),
    top: Math.max(margin, viewport.height - panel.height - margin),
  }
}
